/**
 * The drawer's body: the active tab's content, or the loading skeleton. In
 * master mode the General tab is the master's (no schedule, no slots) and
 * List groups does not exist.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { StepToneProvider } from '@/components/ui';
import { ListGroupScopeProvider } from '@/features/list-groups';
import { cn } from '@/lib/utils';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { CreativeTab } from '../creative/CreativeTab';
import type { EditorTabKey } from '../EditorTabs';
import { GatewaysTab } from '../gateways/GatewaysTab';
import { GeneralSlotsTab } from '../general/GeneralSlotsTab';
import { GroupsTab } from '../groups/GroupsTab';
import { MasterGeneralTab } from '../master/MasterGeneralTab';
import { OptionsTab } from '../options/OptionsTab';
import type { ContainerEditor } from '../state/useContainerEditor';
import { TAB_CHROME } from '../tabs/tab-chrome';
import { VerdictsTab } from '../verdicts/VerdictsTab';
import { AutoSendTab } from '../autosend/AutoSendTab';
import { CountryClockProvider, useCountryClock } from '../../shared/time/country-clock-context';

interface Props {
  tab: EditorTabKey;
  mode: 'container' | 'master';
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  countryName: string;
  containerId: string | null;
  /** Master mode: the campaign is shown, never picked. */
  campaignLocked?: boolean;
  loading: boolean;
}

/** Every tab under the country's clock (15/10/2026): Danish times show the country's beside them,
 *  on the page's run date (the board's preview day) so the editor and the board convert alike. */
export const EditorBody: FC<Props> = (props) => {
  const page = useCountryClock();
  const tone = TAB_CHROME[props.tab];
  return (
    <CountryClockProvider value={{ tz: props.plan.recipientTimezone, flag: countryToFlagEmoji(props.countryName), runDate: page.runDate }}>
      {/* 19/10/2026 — every group in the editor reads this container's campaign's lists. */}
      <ListGroupScopeProvider campaignWiserId={props.editor.draft.campaignWiserId}>
        <StepToneProvider tone={tone.step}>
          <div data-tone={props.tab} className={cn('rounded-xl px-3 py-3 sm:px-4', tone.panel)}>
            <EditorTab {...props} />
          </div>
        </StepToneProvider>
      </ListGroupScopeProvider>
    </CountryClockProvider>
  );
};

const EditorTab: FC<Props> = ({ tab, mode, editor, plan, countryName, containerId, campaignLocked = false, loading }) => {
  if (loading)
    return (
      <div className="flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-xl bg-muted/60" />
        ))}
      </div>
    );
  if (tab === 'general')
    return mode === 'master' ? (
      <MasterGeneralTab editor={editor} plan={plan} containerId={containerId} campaignLocked={campaignLocked} />
    ) : (
      <GeneralSlotsTab editor={editor} plan={plan} containerId={containerId} />
    );
  if (tab === 'groups') return <GroupsTab editor={editor} plan={plan} countryName={countryName} containerId={containerId} />;
  if (tab === 'gateways') return <GatewaysTab editor={editor} plan={plan} containerId={containerId} />;
  if (tab === 'creative') return <CreativeTab editor={editor} countryKey={plan.countryKey} containerId={containerId} />;
  if (tab === 'verdicts') return <VerdictsTab editor={editor} plan={plan} containerId={containerId} />;
  if (tab === 'autosend') return <AutoSendTab editor={editor} plan={plan} containerId={containerId} />;
  return <OptionsTab editor={editor} countryKey={plan.countryKey} />;
};
